import { useEffect } from "react";

/**
 * useLiveUpdates (Iter33) — opens a WebSocket to /api/live/updates and
 * dispatches every incoming event as a `kobolog:live` CustomEvent on window.
 *
 * Components subscribe with:
 *   useEffect(() => {
 *     const handler = (e) => { if (e.detail?.kind === "transaction") refetch(); };
 *     window.addEventListener("kobolog:live", handler);
 *     return () => window.removeEventListener("kobolog:live", handler);
 *   }, []);
 *
 * The hook auto-reconnects with exponential backoff up to 30 s. It relies on
 * a `token` query param — for cookie-only sessions we read it from a helper
 * (currently we don't expose the raw JWT to JS anymore, so this hook attempts
 * cookie-based auth first, falls back to no-op when no auth is available).
 */
const WS_MAX_BACKOFF_MS = 30_000;
const WS_MIN_BACKOFF_MS = 1_000;

export default function useLiveUpdates(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    let ws = null;
    let closed = false;
    let backoff = WS_MIN_BACKOFF_MS;
    let reconnectTimer = null;

    const BASE = process.env.REACT_APP_BACKEND_URL || "";
    // Convert https → wss / http → ws.
    const wsBase = BASE.replace(/^http/, "ws");
    // Iter33: we no longer put the JWT in localStorage; the httpOnly cookie
    // carries it. The FastAPI WS route reads `kobolog_token` cookie too, so
    // simply omitting `?token=` lets the browser attach the cookie via the
    // origin's same-site policy.
    const legacyToken = (() => {
      try { return localStorage.getItem("kudi_token") || ""; } catch { return ""; }
    })();
    const url = legacyToken
      ? `${wsBase}/api/live/updates?token=${encodeURIComponent(legacyToken)}`
      : `${wsBase}/api/live/updates`;

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(url);
      } catch (_) {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => { backoff = WS_MIN_BACKOFF_MS; };
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          window.dispatchEvent(new CustomEvent("kobolog:live", { detail: payload }));
        } catch (_) { /* ignore malformed frames */ }
      };
      ws.onerror = () => { /* onclose handles the reconnect */ };
      ws.onclose = (evt) => {
        // 4401 = server rejected auth — don't retry (they'll need to log in again).
        if (evt && evt.code === 4401) return;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (closed) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, WS_MAX_BACKOFF_MS);
    };

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      try { if (ws && ws.readyState !== WebSocket.CLOSED) ws.close(); } catch (_) { /* ignore */ }
    };
  }, [enabled]);
}
